
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Copy, RefreshCw, Eye, EyeOff, Code } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

const texts = {
  fr: {
    title: "API de Vote",
    tokenLabel: "Token API",
    keepSecret: "⚠️ Gardez cette clé secrète et sécurisée",
    serverUUIDLabel: "Server UUID",
    apiUrlLabel: "URL de l'API",
    urlWithTokenPlaceholder: "URL avec token",
    urlWithUUIDPlaceholder: "URL avec UUID",
    replaceUserIp: "Remplacez {user_ip} par l'IP de l'utilisateur",
    docsTitle: "Documentation API",
    usageTitle: "🎯 Usage Simple",
    usageDesc: "Cette API vous permet de vérifier si un joueur a voté récemment pour votre serveur. Utilisez-la pour donner des récompenses automatiques aux votants.",
    endpointFormatTitle: "Format du point d'accès",
    endpointPath: "GET /servers/{server_token_ou_uuid}/votes/{user_ip}",
    successResponseTitle: "✅ Réponse de succès (Vote trouvé)",
    failResponseTitle: "❌ Réponse d'échec (Aucun vote)",
    failResponseJsonMessage: "Aucun vote trouvé pour cette IP",
    phpExampleTitle: "Exemple PHP complet",
    phpIntro: "API de Vote - Vérifier si un utilisateur a voté récemment",
    phpServerTokenComment: "Token de votre serveur (ou utilisez le UUID)",
    phpUrlComment: "Appel API simple - aucune clé API requise",
    phpUserIpComment: "IP de l'utilisateur",
    phpIfVoted: "✅ L'utilisateur a voté récemment !",
    phpEchoUser: "Pseudo: ",
    phpEchoVotedAt: "Voté le: ",
    phpEchoNext: "Peut voter dans: ",
    phpSeconds: "secondes",
    phpElseNotVoted: "❌ Aucun vote récent trouvé pour cette IP",
    phpRewardComment: "Vous pouvez maintenant donner des récompenses à ce joueur",
    phpGiveRewardComment: "Donner récompense",
    keyPointsTitle: "✨ Points clés",
    key1: "• ✅ Aucune clé API requise - Simple appel HTTP GET",
    key2: "• ⏰ Fenêtre de validité: 2 heures après le vote",
    key3: "• 🔒 Sécurisé: Hachage SHA-256 des IPs",
    key4: "• 🚀 Deux options: Token serveur OU UUID serveur",
    key5: "• 📊 Données utiles: Pseudo, timestamp, temps restant",
    key6: "• 🌐 IMPORTANT: Utilisez l'IP PUBLIQUE du joueur (pas 172.x, 192.168.x, 10.x)",
    recoTitle: "⚠️ Recommandations",
    rec1: "• Testez avec votre propre IP avant mise en production",
    rec2: "• Gardez votre token secret (ne pas l'exposer côté client)",
    rec3: "• Gérez les erreurs réseau dans votre code",
    rec4: "• Limitez les appels API (cache local recommandé)",
    rec5: "• ⚠️ CRITIQUE: Utilisez TOUJOURS l'IP PUBLIQUE du joueur",
    rec6: "• Les IPs privées (172.x, 192.168.x, 10.x) ne fonctionneront PAS",
    rec7: "• Le joueur doit voter sur le site AVANT que vous puissiez vérifier",
  },
  en: {
    title: "Vote API",
    tokenLabel: "API Token",
    keepSecret: "⚠️ Keep this key secret and secure",
    serverUUIDLabel: "Server UUID",
    apiUrlLabel: "API URL",
    urlWithTokenPlaceholder: "URL with token",
    urlWithUUIDPlaceholder: "URL with UUID",
    replaceUserIp: "Replace {user_ip} with the user's IP",
    docsTitle: "API Documentation",
    usageTitle: "🎯 Simple Usage",
    usageDesc: "This API lets you check if a player voted recently for your server. Use it to grant automatic rewards to voters.",
    endpointFormatTitle: "Endpoint format",
    endpointPath: "GET /servers/{server_token_or_uuid}/votes/{user_ip}",
    successResponseTitle: "✅ Success response (Vote found)",
    failResponseTitle: "❌ Failure response (No vote)",
    failResponseJsonMessage: "No vote found for this IP",
    phpExampleTitle: "Full PHP example",
    phpIntro: "Vote API - Check if a user has voted recently",
    phpServerTokenComment: "Your server token (or use the UUID)",
    phpUrlComment: "Simple API call - no API key required",
    phpUserIpComment: "User's IP",
    phpIfVoted: "✅ The user has voted recently!",
    phpEchoUser: "Username: ",
    phpEchoVotedAt: "Voted at: ",
    phpEchoNext: "Can vote again in: ",
    phpSeconds: "seconds",
    phpElseNotVoted: "❌ No recent vote found for this IP",
    phpRewardComment: "You can now give rewards to this player",
    phpGiveRewardComment: "Give reward",
    keyPointsTitle: "✨ Key points",
    key1: "• ✅ No API key required - Simple HTTP GET",
    key2: "• ⏰ Validity window: 2 hours after the vote",
    key3: "• 🔒 Secure: SHA-256 IP hashing",
    key4: "• 🚀 Two options: Server token OR server UUID",
    key5: "• 📊 Useful data: Username, timestamp, remaining time",
    key6: "• 🌐 IMPORTANT: Use the player's PUBLIC IP (not 172.x, 192.168.x, 10.x)",
    recoTitle: "⚠️ Recommendations",
    rec1: "• Test with your own IP before going live",
    rec2: "• Keep your token secret (do not expose on the client)",
    rec3: "• Handle network errors in your code",
    rec4: "• Limit API calls (local cache recommended)",
    rec5: "• ⚠️ CRITICAL: ALWAYS use the player's PUBLIC IP",
    rec6: "• Private IPs (172.x, 192.168.x, 10.x) will NOT work",
    rec7: "• The player must vote on the site BEFORE you can verify",
  },
  es: {
    title: "API de Voto",
    tokenLabel: "Token de la API",
    keepSecret: "⚠️ Mantén esta clave secreta y segura",
    serverUUIDLabel: "UUID del servidor",
    apiUrlLabel: "URL de la API",
    urlWithTokenPlaceholder: "URL con token",
    urlWithUUIDPlaceholder: "URL con UUID",
    replaceUserIp: "Reemplace {user_ip} por la IP del usuario",
    docsTitle: "Documentación de la API",
    usageTitle: "🎯 Uso simple",
    usageDesc: "Esta API permite verificar si un jugador ha votado recientemente por tu servidor. Úsala para dar recompensas automáticas a los votantes.",
    endpointFormatTitle: "Formato del endpoint",
    endpointPath: "GET /servers/{server_token_o_uuid}/votes/{user_ip}",
    successResponseTitle: "✅ Respuesta de éxito (Voto encontrado)",
    failResponseTitle: "❌ Respuesta de error (Sin voto)",
    failResponseJsonMessage: "No se encontró un voto para esta IP",
    phpExampleTitle: "Ejemplo PHP completo",
    phpIntro: "API de Voto - Verificar si un usuario ha votado recientemente",
    phpServerTokenComment: "Token de tu servidor (o usa el UUID)",
    phpUrlComment: "Llamada API simple - no se requiere clave API",
    phpUserIpComment: "IP del usuario",
    phpIfVoted: "✅ ¡El usuario ha votado recientemente!",
    phpEchoUser: "Usuario: ",
    phpEchoVotedAt: "Votó el: ",
    phpEchoNext: "Puede volver a votar en: ",
    phpSeconds: "segundos",
    phpElseNotVoted: "❌ No se encontró un voto reciente para esta IP",
    phpRewardComment: "Ahora puedes dar recompensas a este jugador",
    phpGiveRewardComment: "Dar recompensa",
    keyPointsTitle: "✨ Puntos clave",
    key1: "• ✅ No se requiere clave API - Simple HTTP GET",
    key2: "• ⏰ Ventana de validez: 2 horas después del voto",
    key3: "• 🔒 Seguro: Hash de IP SHA-256",
    key4: "• 🚀 Dos opciones: Token del servidor O UUID del servidor",
    key5: "• 📊 Datos útiles: Usuario, timestamp, tiempo restante",
    key6: "• 🌐 IMPORTANTE: Use la IP PÚBLICA del jugador (no 172.x, 192.168.x, 10.x)",
    recoTitle: "⚠️ Recomendaciones",
    rec1: "• Prueba con tu propia IP antes de producción",
    rec2: "• Mantén tu token secreto (no lo expongas en el cliente)",
    rec3: "• Maneja errores de red en tu código",
    rec4: "• Limita las llamadas a la API (se recomienda caché local)",
    rec5: "• ⚠️ CRÍTICO: USA SIEMPRE la IP PÚBLICA del jugador",
    rec6: "• Las IPs privadas (172.x, 192.168.x, 10.x) NO funcionarán",
    rec7: "• El jugador debe votar en el sitio ANTES de que puedas verificar",
  }
} as const;

interface VoteAPIManagerProps {
  serverId: string;
  serverName: string;
  apiToken?: string;
}

export const VoteAPIManager = ({ serverId, serverName, apiToken }: VoteAPIManagerProps) => {
  const { t, language } = useLanguage();
  const L = texts[language] || texts.fr;
  const [token, setToken] = useState(apiToken || '');
  const [showToken, setShowToken] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Securely fetch the API token via RPC if not already provided.
  // The `servers.api_token` column is no longer publicly readable.
  useEffect(() => {
    if (token || !serverId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('get_server_api_token', {
        p_server_id: serverId,
      });
      if (!cancelled && !error && data) setToken(data as string);
    })();
    return () => { cancelled = true; };
  }, [serverId, token]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t('voteApi.copySuccess'),
      description: t('voteApi.copySuccessDesc'),
    });
  };

  const regenerateToken = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.rpc('regenerate_server_api_token', {
        p_server_id: serverId
      });

      if (error) throw error;

      setToken(data);
      toast({
        title: t('voteApi.tokenRegenerated'),
        description: t('voteApi.tokenRegeneratedDesc'),
      });
    } catch (error) {
      console.error('Erreur lors de la régénération du token:', error);
      toast({
        title: t('voteApi.error'),
        description: t('voteApi.regenerateError'),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const apiUrlByToken = `https://erqsoqulvuxrhsokiarz.supabase.co/functions/v1/vote-api/servers/${token}/votes/{user_ip}`;
  const apiUrlById = `https://erqsoqulvuxrhsokiarz.supabase.co/functions/v1/vote-api/servers/${serverId}/votes/{user_ip}`;
  
  const getExampleCode = () => `<?php
// ${L.phpIntro}
$server_token = "${token}"; // ${L.phpServerTokenComment}
$user_ip = $_SERVER['REMOTE_ADDR']; // ${L.phpUserIpComment}

// ${L.phpUrlComment}
$url = "https://erqsoqulvuxrhsokiarz.supabase.co/functions/v1/vote-api/servers/$server_token/votes/$user_ip";
$json = file_get_contents($url);
$response = json_decode($json);

if ($response->success) {
    echo "${L.phpIfVoted}\\n";
    $data = $response->data;
    echo "${L.phpEchoUser}" . $data->username . "\\n";
    echo "${L.phpEchoVotedAt}" . date('Y-m-d H:i:s', $data->voted_at) . "\\n";
    echo "${L.phpEchoNext}" . $data->next_vote_seconds . " ${L.phpSeconds}\\n";
    
    // ${L.phpRewardComment}
    if ($data->next_vote_seconds > 0) {
        // ${L.phpGiveRewardComment}
        giveReward($data->username);
    }
} else {
    echo "${L.phpElseNotVoted}\\n";
    echo "Message: " . $response->message . "\\n";
}
?>`;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            {L.title} - {serverName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="api-token">{L.tokenLabel}</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="api-token"
                type={showToken ? "text" : "password"}
                value={token}
                readOnly
                className="font-mono"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(token)}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={regenerateToken}
                disabled={isGenerating}
              >
                <RefreshCw className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{L.keepSecret}</p>
          </div>

          <div>
            <Label>{L.serverUUIDLabel}</Label>
            <div className="flex gap-2 mt-1">
              <Input value={serverId} readOnly className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(serverId)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <Label>{L.apiUrlLabel}</Label>
            <div className="flex flex-col gap-2 mt-1">
              <div className="flex gap-2">
                <Input
                  value={apiUrlByToken}
                  readOnly
                  className="font-mono text-xs"
                  placeholder={L.urlWithTokenPlaceholder}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(apiUrlByToken)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={apiUrlById}
                  readOnly
                  className="font-mono text-xs"
                  placeholder={L.urlWithUUIDPlaceholder}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(apiUrlById)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{L.replaceUserIp}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{L.docsTitle}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">{L.usageTitle}</h4>
            <p className="text-blue-700 text-sm">{L.usageDesc}</p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">{L.endpointFormatTitle}</h4>
            <code className="bg-slate-800 border border-slate-600 p-2 rounded text-sm block text-slate-200">
              {L.endpointPath}
            </code>
          </div>

          <div>
            <h4 className="font-semibold mb-2">{L.successResponseTitle}</h4>
            <pre className="bg-slate-800 border border-slate-600 p-3 rounded text-xs overflow-x-auto text-slate-200">
{`{
  "success": true,
  "data": {
    "username": "ExamplePlayer",
    "voted_at": 1693847474,
    "next_vote_seconds": 3250,
    "is_connected": true,
    "is_subscriber": false,
    "server_name": "${serverName}"
  }
}`}
            </pre>
          </div>

          <div>
            <h4 className="font-semibold mb-2">{L.failResponseTitle}</h4>
            <pre className="bg-slate-800 border border-slate-600 p-3 rounded text-xs overflow-x-auto text-slate-200">{`{
  "success": false,
  "message": "${L.failResponseJsonMessage}"
}`}</pre>
          </div>

          <div>
            <h4 className="font-semibold mb-2">{L.phpExampleTitle}</h4>
            <div className="relative">
              <pre className="bg-slate-800 border border-slate-600 p-3 rounded text-xs overflow-x-auto text-slate-200">
                {getExampleCode()}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(getExampleCode())}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">{L.keyPointsTitle}</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>{L.key1}</li>
              <li>{L.key2}</li>
              <li>{L.key3}</li>
              <li>{L.key4}</li>
              <li>{L.key5}</li>
              <li className="font-bold">{L.key6}</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-2">{L.recoTitle}</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>{L.rec1}</li>
              <li>{L.rec2}</li>
              <li>{L.rec3}</li>
              <li>{L.rec4}</li>
              <li className="font-bold text-red-700">{L.rec5}</li>
              <li className="font-bold text-red-700">{L.rec6}</li>
              <li className="font-bold">{L.rec7}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
